import React, { useState, useEffect, useRef, useMemo } from 'react';
import UnifiedRoomLayout from './UnifiedRoomLayout';
import PiPVideoFeed from './PiPVideoFeed';
import CafeWhiteboard from './CafeWhiteboard';
import CafeDocViewer from './CafeDocViewer';
import CafeMediaPlayer from './CafeMediaPlayer';
import GridViewLayout from './GridViewLayout';
import SpotlightLayout from './SpotlightLayout';
import SplitViewLayout from './SplitViewLayout';
import ToastStack from './Toast.jsx';
import { useVcafeWebRTC } from '../../../hooks/useWebRTC';
import { useMediaSettings } from '../../../contexts/MediaSettingsContext';
import { pdfjs } from 'react-pdf';
import authFetch from '../../../utils/authFetch';
import './FocusAffordance.css';

const VideoChatRoom = ({ room, onLeave, user, supabase }) => {
  // Real-time chat state
  const [messages, setMessages] = useState([]);
  const messageSubscriptionRef = useRef(null);

  // Real-time participants state
  const [participantList, setParticipantList] = useState([]);
  const participantSubscriptionRef = useRef(null);

  // Layout & Focus state
  const [layoutMode, setLayoutMode] = useState('conference'); // 'conference' | 'presentation'
  const [focusMode, setFocusMode] = useState('grid'); // 'grid' | 'spotlight'
  const [presentationContentType, setPresentationContentType] = useState('whiteboard'); // 'whiteboard' | 'docs' | 'screen' | 'split'
  const [presenterId, setPresenterId] = useState(room?.presenter_id || null);
  const [roomMediaState, setRoomMediaState] = useState(null);

  const isPresenter = presenterId === user?.id;
  const someonePresenting = !!presenterId;
  const isAudience = someonePresenting && !isPresenter;

  // Sidebar state
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  // Bottom participant strip state (presentation mode)
  const [bottomStripExpanded, setBottomStripExpanded] = useState(true);

  // Local media UI state
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [screenOn, setScreenOn] = useState(false);

  // Use global media settings
  const { openMediaSettings, settings: globalMediaSettings } = useMediaSettings() || {};
  const [mediaSettings, setMediaSettings] = useState(null);

  // Timer
  const [seconds, setSeconds] = useState(0);

  const [activeSpeakerId, setActiveSpeakerId] = useState(null);
  const [localMicActive, setLocalMicActive] = useState(false);

  // Docs presentation state (reads from room/current_doc_id for now)
  const [currentDoc, setCurrentDoc] = useState(null);
  const [currentDocPage, setCurrentDocPage] = useState(room?.current_doc_page || 1);
  const [totalDocPages, setTotalDocPages] = useState(1);
  const [docOptions, setDocOptions] = useState([]);
  const fileInputRef = useRef(null);

  const [mountedRoomDocs, setMountedRoomDocs] = useState([]); // vcafe_room_docs rows
  const [activeRoomDocId, setActiveRoomDocId] = useState(null); // document_id
  const [showDocsTray, setShowDocsTray] = useState(false);
  const roomDocsSubscriptionRef = useRef(null);
  const pagePreviewThrottleRef = useRef({ timer: null, lastKey: '', lastAt: 0 });

  const activeRoomDocRow = useMemo(() => {
    if (!activeRoomDocId) return null;
    return mountedRoomDocs.find(r => String(r.document_id) === String(activeRoomDocId)) || null;
  }, [activeRoomDocId, mountedRoomDocs]);

  const activeDocRotation = useMemo(() => {
    const v = activeRoomDocRow?.view_state || {};
    const rot = typeof v.rotation === 'number' ? v.rotation : 0;
    return rot === 90 ? 90 : 0;
  }, [activeRoomDocRow]);

  const [mediaAssets, setMediaAssets] = useState([]);
  const mediaFileInputRef = useRef(null);

  const mediaChoices = useMemo(() => {
    return mediaAssets
      .filter(m => m.media_kind === 'audio' || m.media_kind === 'video')
      .slice()
      .sort((a, b) => {
        if (a.media_kind !== b.media_kind) {
          // Group audio before video
          return a.media_kind === 'audio' ? -1 : 1;
        }
        const ta = (a.title || a.filename || '').toLowerCase();
        const tb = (b.title || b.filename || '').toLowerCase();
        return ta.localeCompare(tb);
      });
  }, [mediaAssets]);

  const audioMediaChoices = useMemo(
    () => mediaChoices.filter(m => m.media_kind === 'audio'),
    [mediaChoices]
  );

  const [showAudioPanel, setShowAudioPanel] = useState(false);

  const bgAudioMediaId = roomMediaState?.bg_audio_media_id || null;

  const bgAudioMedia = useMemo(
    () => (bgAudioMediaId ? audioMediaChoices.find(m => m.id === bgAudioMediaId) || null : null),
    [audioMediaChoices, bgAudioMediaId]
  );

  const bgAudioUrl = useMemo(() => {
    if (!bgAudioMedia) return null;
    try {
      const { data } = supabase
        .storage
        .from(bgAudioMedia.bucket || 'media')
        .getPublicUrl(bgAudioMedia.path);
      return data?.publicUrl || null;
    } catch {
      return null;
    }
  }, [bgAudioMedia, supabase]);

  const bgAudioPlayState = roomMediaState?.bg_audio_play_state || 'stopped';
  const bgAudioPositionSec = roomMediaState?.bg_audio_position_sec || 0;
  const bgAudioPlaybackRate = roomMediaState?.bg_audio_playback_rate || 1;

  // Document annotations state
  const [docAnnotations, setDocAnnotations] = useState([]);
  const annotationSubscriptionRef = useRef(null);

  // Toasts
  const [toasts, setToasts] = useState([]);
  const activeToastKeys = useRef(new Set());

  const pushToast = (message, type = 'info') => {
    const key = `${type}:${message}`;
    if (activeToastKeys.current.has(key)) return;
    const id = Date.now() + Math.random();
    activeToastKeys.current.add(key);
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      closeToast(id);
      activeToastKeys.current.delete(key);
    }, 3000);
  };

  const handleToggleDocRotation = async () => {
    if (!room?.id || !activeRoomDocId) return;
    if (!isPresenter) return;

    const nextRotation = activeDocRotation === 90 ? 0 : 90;
    const prev = activeRoomDocRow?.view_state || {};
    const next = { ...prev, rotation: nextRotation };

    try {
      await supabase
        .from('vcafe_room_docs')
        .update({ view_state: next })
        .eq('room_id', room.id)
        .eq('document_id', activeRoomDocId);
    } catch (err) {
      console.error('Error updating doc rotation', err);
    }
  };

  const handleUnmountRoomDoc = async (docId) => {
    if (!room?.id || !docId) return;
    if (!isPresenter) return;

    try {
      // Best-effort: delete room preview thumbnail so we don't accumulate orphaned previews.
      try {
        await authFetch('/api/thumbnails/document/cleanup', {
          method: 'POST',
          body: { documentId: docId, roomId: room.id, scope: 'room_only' },
        });
      } catch {
        // best-effort
      }

      await supabase
        .from('vcafe_room_docs')
        .delete()
        .eq('room_id', room.id)
        .eq('document_id', docId);

      const remaining = mountedRoomDocs.filter(r => String(r.document_id) !== String(docId));
      const wasActive = String(activeRoomDocId || '') === String(docId);
      if (wasActive) {
        const next = remaining[0]?.document_id || null;
        if (next) {
          await supabase.rpc('set_active_room_doc', {
            p_room_id: room.id,
            p_document_id: next,
          });
        } else {
          setActiveRoomDocId(null);
          setCurrentDoc(null);
          setCurrentDocPage(1);
          try {
            await supabase
              .from('rooms')
              .update({ current_doc_id: null, current_doc_page: 1 })
              .eq('id', room.id);
          } catch {
            // best-effort
          }
        }
      }
    } catch (err) {
      console.error('Error unmounting room doc', err);
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!docId || !user?.id) return;

    const doc = docOptions.find(d => String(d.id) === String(docId));
    if (!doc) return;

    try {
      // Best-effort: cleanup all thumbnails (base + all room previews) via server/service-role.
      try {
        await authFetch('/api/thumbnails/document/cleanup', {
          method: 'POST',
          body: { documentId: doc.id, scope: 'all_rooms' },
        });
      } catch {
        // best-effort
      }

      if (doc.bucket && doc.path) {
        try {
          await supabase
            .storage
            .from(doc.bucket || 'documents')
            .remove([doc.path]);
        } catch {
          // best-effort: ignore storage delete failures
        }
      }

      if (doc.thumbnail_path) {
        try {
          await supabase
            .storage
            .from(doc.thumbnail_bucket || doc.bucket || 'documents')
            .remove([doc.thumbnail_path]);
        } catch {
          // best-effort
        }
      }

      // Delete doc record (RLS will enforce ownership)
      await supabase
        .from('vcafe_documents')
        .delete()
        .eq('id', doc.id)
        .eq('owner_id', user.id);

      // Best-effort: unmount from this room if mounted
      if (room?.id) {
        try {
          await supabase
            .from('vcafe_room_docs')
            .delete()
            .eq('room_id', room.id)
            .eq('document_id', doc.id);
        } catch {
          // best-effort
        }
      }

      setDocOptions(prev => prev.filter(d => d.id !== doc.id));

      if (currentDoc?.id === doc.id) {
        setCurrentDoc(null);
        setCurrentDocPage(1);
        setActiveRoomDocId(null);
        if (room?.id) {
          try {
            await supabase
              .from('rooms')
              .update({ current_doc_id: null, current_doc_page: 1 })
              .eq('id', room.id);
          } catch {
            // best-effort
          }
        }
      }

      pushToast('Document removed', 'success');
    } catch (err) {
      console.error('Error deleting document', err);
      pushToast('Failed to delete document', 'error');
    }
  };

  const handleSelectDoc = async (docId) => {
    if (!docId) return;
    const doc = docOptions.find(d => String(d.id) === String(docId));
    if (!doc || !room?.id) return;
    if (room?.isEvent && !room?.hasLinkedRoom) return;

    // Multi-doc: ensure this doc is mounted in vcafe_room_docs, then set active via RPC
    try {
      const existing = mountedRoomDocs.find(r => String(r.document_id) === String(docId)) || null;
      if (!existing && isPresenter) {
        const nextPos = mountedRoomDocs.length > 0
          ? Math.max(...mountedRoomDocs.map(r => r.position || 0)) + 1
          : 0;
        await supabase
          .from('vcafe_room_docs')
          .upsert({
            room_id: room.id,
            document_id: docId,
            position: nextPos,
            last_page: 1,
            is_active: false,
            mounted_by: user.id,
          }, {
            onConflict: 'room_id,document_id',
            ignoreDuplicates: true,
          });
      }

      if (isPresenter) {
        await supabase.rpc('set_active_room_doc', {
          p_room_id: room.id,
          p_document_id: docId,
        });
      }
    } catch (err) {
      console.error('Error mounting/activating document', err);
    }

    // Backward-compat pointer (still used by some code paths)
    setCurrentDoc(doc);
    setCurrentDocPage(1);
    setActiveRoomDocId(docId);

    try {
      await supabase
        .from('rooms')
        .update({ current_doc_id: docId, current_doc_page: 1 })
        .eq('id', room.id);
    } catch {
      // best-effort
    }
  };

  const handleUploadDocClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleUploadDocFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file || !user?.id) return;

    const ext = file.name.split('.').pop();
    const safeName = file.name.replace(/[^a-zA-Z0-9_.-]/g, '_');
    const path = `${user.id}/${Date.now()}_${safeName}`;

    if (!pdfjs?.GlobalWorkerOptions?.workerSrc) {
      pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
    }

    const generateThumbnail = async () => {
      // Returns { blob, contentType, thumbPath } or null
      try {
        const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
        const isImage = !!(file.type && file.type.startsWith('image/'));
        const maxSize = 360;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        if (isImage) {
          const bmp = await createImageBitmap(file);
          const scale = Math.min(1, maxSize / Math.max(bmp.width, bmp.height));
          canvas.width = Math.max(1, Math.round(bmp.width * scale));
          canvas.height = Math.max(1, Math.round(bmp.height * scale));
          ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);

          const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
          if (!blob) return null;
          const thumbPath = `document-thumbs/${user.id}/${Date.now()}_${safeName.replace(/\.[^.]+$/, '')}.jpg`;
          return { blob, contentType: 'image/jpeg', thumbPath };
        }

        if (isPdf) {
          const ab = await file.arrayBuffer();
          const pdf = await pdfjs.getDocument({ data: ab }).promise;
          const page = await pdf.getPage(1);

          // Render to a canvas and then resize down
          const baseViewport = page.getViewport({ scale: 1 });
          const scale = Math.min(1, maxSize / Math.max(baseViewport.width, baseViewport.height));
          const viewport = page.getViewport({ scale });
          canvas.width = Math.max(1, Math.round(viewport.width));
          canvas.height = Math.max(1, Math.round(viewport.height));

          await page.render({ canvasContext: ctx, viewport }).promise;

          const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
          if (!blob) return null;
          const thumbPath = `document-thumbs/${user.id}/${Date.now()}_${safeName.replace(/\.[^.]+$/, '')}.jpg`;
          return { blob, contentType: 'image/jpeg', thumbPath };
        }

        return null;
      } catch (err) {
        console.error('Thumbnail generation failed', err);
        return null;
      }
    };

    try {
      const { error: uploadError } = await supabase
        .storage
        .from('documents')
        .upload(path, file, { upsert: false });

      if (uploadError) {
        pushToast('Failed to upload document', 'error');
        return;
      }

      let thumbnail_path = null;
      try {
        const thumb = await generateThumbnail();
        if (thumb?.blob && thumb?.thumbPath) {
          const { error: thumbError } = await supabase
            .storage
            .from('thumbnails')
            .upload(thumb.thumbPath, thumb.blob, {
              upsert: false,
              contentType: thumb.contentType,
              cacheControl: '3600',
            });

          if (!thumbError) {
            thumbnail_path = thumb.thumbPath;
          }
        }
      } catch {
        // best-effort
      }

      const { data: inserted, error: insertError } = await supabase
        .from('vcafe_documents')
        .insert({
          owner_id: user.id,
          bucket: 'documents',
          path,
          title: file.name,
          mime_type: file.type,
          thumbnail_bucket: thumbnail_path ? 'thumbnails' : null,
          thumbnail_path,
        })
        .select('*')
        .single();

      if (insertError || !inserted) {
        pushToast('Failed to register document', 'error');
        return;
      }

      setDocOptions(prev => [...prev, inserted]);
      await handleSelectDoc(inserted.id);

      // Server-side base thumbnail generation (more reusable across apps).
      // Best-effort: if it fails we still keep the client-generated thumbnail.
      try {
        const resp = await authFetch('/api/thumbnails/document', {
          method: 'POST',
          body: { documentId: inserted.id },
        });
        if (resp.ok) {
          const j = await resp.json().catch(() => null);
          if (j?.thumbnail_bucket && j?.thumbnail_path) {
            setDocOptions(prev => prev.map(d => (
              String(d.id) === String(inserted.id)
                ? { ...d, thumbnail_bucket: j.thumbnail_bucket, thumbnail_path: j.thumbnail_path }
                : d
            )));
            setCurrentDoc(prev => (
              prev && String(prev.id) === String(inserted.id)
                ? { ...prev, thumbnail_bucket: j.thumbnail_bucket, thumbnail_path: j.thumbnail_path }
                : prev
            ));
          }
        } else {
          const txt = await resp.text().catch(() => '');
          console.warn('[thumbnails] base thumbnail failed:', resp.status, txt);
        }
      } catch {
        // best-effort
      }

      pushToast('Document uploaded', 'success');
    } catch {
      pushToast('Unexpected error uploading document', 'error');
    }
  };

  const handleUploadMediaClick = () => {
    if (mediaFileInputRef.current) {
      mediaFileInputRef.current.value = '';
      mediaFileInputRef.current.click();
    }
  };

  const handleUploadMediaFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file || !user?.id || !room?.id) return;

    const isAudio = file.type && file.type.startsWith('audio/');
    const isVideo = file.type && file.type.startsWith('video/');
    if (!isAudio && !isVideo) {
      pushToast('Only audio and video files are supported for media upload', 'error');
      return;
    }

    const mediaKind = isAudio ? 'audio' : 'video';
    const safeName = file.name.replace(/[^a-zA-Z0-9_.-]/g, '_');
    const path = `${room.id}/${user.id}/${Date.now()}_${safeName}`;

    try {
      const { error: uploadError } = await supabase
        .storage
        .from('media')
        .upload(path, file, { upsert: false });

      if (uploadError) {
        pushToast('Failed to upload media', 'error');
        return;
      }

      const { data: inserted, error: insertError } = await supabase
        .from('vcafe_media')
        .insert({
          owner_id: user.id,
          context_type: 'vcafe_room',
          context_id: room.id,
          bucket: 'media',
          path,
          filename: file.name,
          mime_type: file.type,
          media_kind: mediaKind,
          title: file.name,
          size_bytes: file.size,
        })
        .select('*')
        .single();

      if (insertError || !inserted) {
        pushToast('Failed to register media', 'error');
        return;
      }

      setMediaAssets(prev => [...prev, inserted]);
      pushToast('Media uploaded', 'success');
    } catch {
      pushToast('Unexpected error uploading media', 'error');
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!mediaId || !user?.id) return;

    const asset = mediaAssets.find(m => String(m.id) === String(mediaId));
    if (!asset) return;

    try {
      if (asset.bucket && asset.path) {
        try {
          await supabase
            .storage
            .from(asset.bucket || 'media')
            .remove([asset.path]);
        } catch {
          // best-effort: ignore storage delete failures
        }
      }

      await supabase
        .from('vcafe_media')
        .delete()
        .eq('id', asset.id)
        .eq('owner_id', user.id);

      setMediaAssets(prev => prev.filter(m => m.id !== asset.id));

      const updates = {};
      if (roomMediaState?.primary_media_id && String(roomMediaState.primary_media_id) === String(asset.id)) {
        updates.primary_media_id = null;
      }
      if (roomMediaState?.secondary_media_id && String(roomMediaState.secondary_media_id) === String(asset.id)) {
        updates.secondary_media_id = null;
      }
      if (roomMediaState?.bg_audio_media_id && String(roomMediaState.bg_audio_media_id) === String(asset.id)) {
        updates.bg_audio_media_id = null;
        updates.bg_audio_play_state = 'stopped';
        updates.bg_audio_position_sec = 0;
        updates.bg_audio_playback_rate = 1;
      }
      if (Object.keys(updates).length > 0) {
        upsertRoomMediaState({
          layout_mode: 'presentation',
          ...updates,
        });
      }

      pushToast('Media removed', 'success');
    } catch (err) {
      console.error('Error deleting media', err);
      pushToast('Failed to delete media', 'error');
    }
  };

  const handleDocPageChange = async (delta) => {
    if (!room?.id || !currentDoc) return;
    if (room?.isEvent && !room?.hasLinkedRoom) return;
    const nextPage = Math.max(1, Math.min(totalDocPages, (currentDocPage || 1) + delta));
    if (nextPage === currentDocPage) return; // no change needed
    setCurrentDocPage(nextPage);

    if (activeRoomDocId && isPresenter) {
      try {
        await supabase
          .from('vcafe_room_docs')
          .update({ last_page: nextPage })
          .eq('room_id', room.id)
          .eq('document_id', activeRoomDocId);
      } catch {
        // best-effort
      }

      // Per-room page preview thumbnail (Option A), generated by the Node server (Option C).
      // Throttled to avoid spamming the server while paging.
      try {
        const key = `${room.id}:${activeRoomDocId}:${nextPage}`;
        const now = Date.now();
        const minIntervalMs = 1200;
        const ref = pagePreviewThrottleRef.current;

        const run = async () => {
          try {
            const resp = await authFetch('/api/thumbnails/document/page-preview', {
              method: 'POST',
              body: { roomId: room.id, documentId: activeRoomDocId, pageNumber: nextPage },
            });
            if (!resp.ok) {
              const txt = await resp.text().catch(() => '');
              console.warn('[thumbnails] page preview failed:', resp.status, txt);
            }
          } catch {
            // best-effort
          }
        };

        if (ref.timer) {
          clearTimeout(ref.timer);
          ref.timer = null;
        }

        const wait = Math.max(0, minIntervalMs - (now - (ref.lastAt || 0)));
        ref.lastKey = key;
        ref.timer = setTimeout(async () => {
          ref.lastAt = Date.now();
          ref.timer = null;
          await run();
        }, wait);
      } catch {
        // best-effort
      }
    }

    try {
      await supabase
        .from('rooms')
        .update({ current_doc_page: nextPage })
        .eq('id', room.id);
    } catch {
      // best-effort
    }
  };

  const fetchMountedRoomDocs = async () => {
    if (!room?.id) return;
    const { data, error } = await supabase
      .from('vcafe_room_docs')
      .select('*')
      .eq('room_id', room.id)
      .order('position', { ascending: true });

    if (error) {
      console.error('Error fetching vcafe_room_docs', error);
      return;
    }

    const rows = Array.isArray(data) ? data : [];
    setMountedRoomDocs(rows);
    const active = rows.find(r => r.is_active) || null;
    setActiveRoomDocId(active?.document_id || null);
  };

  // Load mounted room docs + subscribe to realtime updates
  useEffect(() => {
    if (!room?.id) return;

    fetchMountedRoomDocs();

    if (roomDocsSubscriptionRef.current) {
      try { supabase.removeChannel(roomDocsSubscriptionRef.current); } catch (_) {}
      roomDocsSubscriptionRef.current = null;
    }

    const ch = supabase
      .channel(`vcafe_room_docs:${room.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'vcafe_room_docs', filter: `room_id=eq.${room.id}` },
        () => {
          fetchMountedRoomDocs();
        }
      )
      .subscribe();

    roomDocsSubscriptionRef.current = ch;

    return () => {
      if (roomDocsSubscriptionRef.current) {
        try { supabase.removeChannel(roomDocsSubscriptionRef.current); } catch (_) {}
        roomDocsSubscriptionRef.current = null;
      }
    };
  }, [room?.id, supabase]);

  // One-time migration: if room.current_doc_id exists but vcafe_room_docs is empty, mount it.
  useEffect(() => {
    const migrate = async () => {
      if (!room?.id || !room?.current_doc_id) return;
      if (mountedRoomDocs.length > 0) return;
      if (!isPresenter) return;

      try {
        await supabase
          .from('vcafe_room_docs')
          .upsert({
            room_id: room.id,
            document_id: room.current_doc_id,
            position: 0,
            last_page: room.current_doc_page || 1,
            is_active: false,
            mounted_by: user.id,
          }, {
            onConflict: 'room_id,document_id',
            ignoreDuplicates: true,
          });
        await supabase.rpc('set_active_room_doc', {
          p_room_id: room.id,
          p_document_id: room.current_doc_id,
        });
      } catch (err) {
        console.error('Error migrating current_doc_id into vcafe_room_docs', err);
      }
    };

    migrate();
  }, [room?.id, room?.current_doc_id, room?.current_doc_page, mountedRoomDocs.length, isPresenter, supabase, user?.id]);

  // Keep currentDoc/currentDocPage in sync with vcafe_room_docs active doc
  useEffect(() => {
    if (!activeRoomDocId) return;
    const row = mountedRoomDocs.find(r => String(r.document_id) === String(activeRoomDocId)) || null;
    const doc = docOptions.find(d => String(d.id) === String(activeRoomDocId)) || null;
    if (doc) setCurrentDoc(doc);
    if (row && typeof row.last_page === 'number') {
      setCurrentDocPage(row.last_page || 1);
    }
  }, [activeRoomDocId, mountedRoomDocs, docOptions]);

  const closeToast = (id) => setToasts(prev => prev.filter(t => t.id !== id));

  // Annotation handlers
  const handleAddAnnotation = async (annotation) => {
    if (!user?.id || !currentDoc?.id) return;
    
    const newAnnotation = {
      ...annotation,
      user_id: user.id,
      document_id: currentDoc.id,
      room_id: room?.id || null,
    };

    try {
      const { data, error } = await supabase
        .from('vcafe_doc_annotations')
        .insert(newAnnotation)
        .select()
        .single();
      
      if (error) {
        console.error('Error adding annotation:', error);
        return;
      }
      
      // Optimistically add to local state (realtime will also catch it)
      if (data) {
        setDocAnnotations(prev => [...prev, data]);
      }
    } catch (err) {
      console.error('Error adding annotation:', err);
    }
  };

  const handleDeleteAnnotation = async (annotationId) => {
    if (!annotationId) return;
    
    try {
      await supabase
        .from('vcafe_doc_annotations')
        .delete()
        .eq('id', annotationId);
      
      setDocAnnotations(prev => prev.filter(a => a.id !== annotationId));
    } catch (err) {
      console.error('Error deleting annotation:', err);
    }
  };

  const handleUpdateAnnotation = async (annotationId, newData) => {
    if (!annotationId) return;

    try {
      const { error } = await supabase
        .from('vcafe_doc_annotations')
        .update({ data: newData })
        .eq('id', annotationId);

      if (error) {
        console.error('Error updating annotation:', error);
        return;
      }

      // Optimistically update local state; realtime UPDATE will also arrive for others
      setDocAnnotations(prev => prev.map(a => a.id === annotationId ? { ...a, data: newData } : a));
    } catch (err) {
      console.error('Error updating annotation:', err);
    }
  };

  // --- Data Fetching & Subscriptions ---

  // Fetch and subscribe to annotations for current document
  useEffect(() => {
    if (!currentDoc?.id) {
      setDocAnnotations([]);
      return;
    }

    const fetchAnnotations = async () => {
      const { data, error } = await supabase
        .from('vcafe_doc_annotations')
        .select('*')
        .eq('document_id', currentDoc.id)
        .order('created_at', { ascending: true });
      
      if (!error && data) {
        setDocAnnotations(data);
      }
    };
    fetchAnnotations();

    // Subscribe to realtime annotation changes
    // Note: DELETE events need REPLICA IDENTITY FULL on the table
    const channel = supabase
      .channel(`doc-annotations-${currentDoc.id}`)
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'vcafe_doc_annotations', filter: `document_id=eq.${currentDoc.id}` },
        (payload) => {
          setDocAnnotations(prev => {
            // Avoid duplicates
            if (prev.some(a => a.id === payload.new.id)) return prev;
            return [...prev, payload.new];
          });
        }
      )
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'vcafe_doc_annotations', filter: `document_id=eq.${currentDoc.id}` },
        (payload) => {
          setDocAnnotations(prev => prev.map(a => a.id === payload.new.id ? payload.new : a));
        }
      )
      .on('postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'vcafe_doc_annotations' },
        (payload) => {
          // Filter client-side since DELETE filters can be unreliable
          const deletedId = payload.old?.id;
          if (!deletedId) return;
          setDocAnnotations(prev => {
            // Only remove if the annotation was for this document
            const existing = prev.find(a => a.id === deletedId);
            if (existing && existing.document_id === currentDoc.id) {
              return prev.filter(a => a.id !== deletedId);
            }
            return prev;
          });
        }
      )
      .subscribe();

    annotationSubscriptionRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentDoc?.id, supabase]);

  // Fetch messages
  useEffect(() => {
    if (!room?.id || room?.isEvent) return;
    const fetchMessages = async () => {
      const { data } = await supabase
        .from('room_messages')
        .select('*')
        .eq('room_id', room.id)
        .order('created_at', { ascending: true });
      setMessages(data || []);
    };
    fetchMessages();

    const channel = supabase
      .channel(`room-messages-${room.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'room_messages', filter: `room_id=eq.${room.id}` },
        payload => setMessages(prev => [...prev, payload.new])
      )
      .subscribe();
    messageSubscriptionRef.current = channel;
    return () => supabase.removeChannel(channel);
  }, [room?.id, room?.isEvent, supabase, user?.id]);

  // Keep presenter state in sync with room prop when it changes
  useEffect(() => {
    setPresenterId(room?.presenter_id || null);
  }, [room?.presenter_id]);

  // Join room on mount and leave on unmount
  useEffect(() => {
    if (!room?.id || !user?.id || room?.isEvent) return;

    const joinRoom = async () => {
      try {
        // Check if we already have a participant row for this user/room
        const { data, error } = await supabase
          .from('room_participants')
          .select('id')
          .eq('room_id', room.id)
          .eq('user_id', user.id)
          .limit(1);

        if (error) {
          console.error('Failed to check existing room_participants', error);
        }

        const existing = data && data[0];

        if (!existing) {
          await supabase
            .from('room_participants')
            .insert({
              room_id: room.id,
              user_id: user.id,
              joined_at: new Date().toISOString(),
            });
        } else {
          // If we re-join, clear left_at so user is considered present again
          await supabase
            .from('room_participants')
            .update({ left_at: null })
            .eq('id', existing.id);
        }
      } catch (e) {
        console.error('Failed to join room_participants', e);
      }
    };

    joinRoom();

    return () => {
      // If we were the active presenter, clear presenter state so others are not blocked
      if (presenterId && presenterId === user.id && room?.id) {
        supabase
          .from('rooms')
          .update({ presenter_id: null, presentation_mode: 'grid' })
          .eq('id', room.id)
          .then(() => {})
          .catch(() => {});
      }

      // Leave room on unmount by marking left_at, rather than deleting the row
      supabase
        .from('room_participants')
        .update({ left_at: new Date().toISOString() })
        .eq('room_id', room.id)
        .eq('user_id', user.id)
        .then(() => {})
        .catch(() => {});
    };
  }, [room?.id, user?.id, room?.isEvent, supabase, presenterId]);

  // Fetch participants
  useEffect(() => {
    if (!room?.id || room?.isEvent) return;
    const fetchParticipants = async () => {
      const { data, error } = await supabase
        .from('room_participants')
        .select('user_id, left_at, profiles(display_name, email, avatar_url)')
        .eq('room_id', room.id)
        .is('left_at', null); // only currently present users

      if (error) {
        console.error('Error fetching participants', error);
      }

      const byUser = new Map();

      (data || []).forEach((p, idx) => {
        if (!p || !p.user_id) return;

        const prof = p.profiles || {};
        const name = prof.display_name || prof.email || `User ${idx + 1}`;
        byUser.set(p.user_id, {
          id: p.user_id,
          name,
          avatar: (name[0] || '?').toUpperCase(),
          muted: false,
          camOn: true, // Mock
          isSelf: p.user_id === user?.id,
        });
      });

      setParticipantList(Array.from(byUser.values()));
    };
    fetchParticipants();

    const channel = supabase
      .channel(`room-participants-${room.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'room_participants', filter: `room_id=eq.${room.id}` },
        () => fetchParticipants()
      )
      .subscribe();
    participantSubscriptionRef.current = channel;
    return () => supabase.removeChannel(channel);
  }, [room?.id, room?.isEvent, supabase]);

  // Timer
  useEffect(() => {
    const interval = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  // Subscribe to room-level presentation changes so audience follows presenter
  useEffect(() => {
    if (!room?.id) return;
    if (room?.isEvent && !room?.hasLinkedRoom) return;

    // Fetch initial room state to sync presenter/layout on join
    const fetchRoomState = async () => {
      const { data: roomData, error } = await supabase
        .from('rooms')
        .select('id, presenter_id, presentation_mode, current_doc_id, current_doc_page')
        .eq('id', room.id)
        .single();

      if (error) return;
      if (!roomData) return;

      // Detect stale presenter: presenter_id set but that user is no longer an active participant
      let effectivePresenterId = roomData.presenter_id;
      if (roomData.presenter_id) {
        const { data: activeRows } = await supabase
          .from('room_participants')
          .select('user_id')
          .eq('room_id', room.id)
          .eq('user_id', roomData.presenter_id)
          .is('left_at', null)
          .limit(1);

        const hasActivePresenter = activeRows && activeRows.length > 0;
        if (!hasActivePresenter) {
          effectivePresenterId = null;
          // Best-effort: clear stale presenter in DB
          await supabase
            .from('rooms')
            .update({ presenter_id: null, presentation_mode: 'grid' })
            .eq('id', room.id);
        }
      }

      setPresenterId(effectivePresenterId || null);

      const isSelfPresenter = effectivePresenterId && effectivePresenterId === user.id;

      if (!isSelfPresenter && effectivePresenterId) {
        setLayoutMode('presentation');
        if (roomData.presentation_mode && roomData.presentation_mode !== 'grid') {
          setPresentationContentType(roomData.presentation_mode);
        }
      }

      if (typeof roomData.current_doc_page === 'number') {
        setCurrentDocPage(roomData.current_doc_page || 1);
      }
    };

    fetchRoomState();

    const channel = supabase
      .channel(`rooms-sync-${room.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'rooms', filter: `id=eq.${room.id}` },
        async payload => {
          const updated = payload.new;

          // Keep local presenterId in sync
          setPresenterId(updated.presenter_id || null);

          const isSelfPresenter = updated.presenter_id && updated.presenter_id === user.id;

          if (!isSelfPresenter && updated.presenter_id) {
            // We are audience while someone else is presenting
            setLayoutMode('presentation');
            if (updated.presentation_mode && updated.presentation_mode !== 'grid') {
              setPresentationContentType(updated.presentation_mode);
            }
          }

          if (!updated.presenter_id) {
            // No presenter: fall back to conference grid
            setLayoutMode('conference');
            setPresentationContentType('whiteboard');
          }

          // Keep docs in sync with room state
          if (typeof updated.current_doc_page === 'number') {
            setCurrentDocPage(updated.current_doc_page || 1);
          }

          // Also sync current_doc_id: load doc metadata when it changes
          if (updated.current_doc_id) {
            const { data: docRows, error: docError } = await supabase
              .from('vcafe_documents')
              .select('*')
              .eq('id', updated.current_doc_id)
              .limit(1);

            if (docError) {
              console.error('Error syncing current doc metadata', docError);
              setCurrentDoc(null);
            } else {
              const doc = (docRows && docRows[0]) || null;
              setCurrentDoc(doc);
            }
          } else {
            setCurrentDoc(null);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [room?.id, supabase, user.id]);

  // Audience-side safety net: if presenter stops and realtime misses the update,
  // periodically poll the room row while we are in presentation mode and not presenter.
  useEffect(() => {
    if (!room?.id) return;
    if (room?.isEvent && !room?.hasLinkedRoom) return;
    if (layoutMode !== 'presentation') return;

    let cancelled = false;
    let intervalId = null;

    const pollRoom = async () => {
      const amPresenter = presenterId && presenterId === user.id;
      if (amPresenter) return;

      try {
        const { data, error } = await supabase
          .from('rooms')
          .select('presenter_id, presentation_mode')
          .eq('id', room.id)
          .single();

        if (cancelled || error || !data) return;

        if (!data.presenter_id) {
          setPresenterId(null);
          setLayoutMode('conference');
          setPresentationContentType('whiteboard');
        }
      } catch {
        // best-effort; ignore polling errors
      }
    };

    // Poll immediately and then periodically
    pollRoom();
    intervalId = setInterval(pollRoom, 3000);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [room?.id, supabase, layoutMode, presenterId, user.id]);

  useEffect(() => {
    if (!room?.id) return;

    const loadMediaAssets = async () => {
      const { data, error } = await supabase
        .from('vcafe_media')
        .select('*')
        .eq('context_type', 'vcafe_room')
        .eq('context_id', room.id)
        .order('created_at', { ascending: true });

      if (!error && data) {
        setMediaAssets(data);
      }
    };

    loadMediaAssets();
  }, [room?.id, supabase]);

  useEffect(() => {
    if (!room?.id) return;
    if (room?.isEvent && !room?.hasLinkedRoom) return;

    let cancelled = false;

    const loadInitialState = async () => {
      const { data, error } = await supabase
        .from('vcafe_room_media_state')
        .select('*')
        .eq('room_id', room.id)
        .maybeSingle();

      if (error) {
        console.error('Error loading room media state', error);
        return;
      }

      if (!cancelled && data) {
        setRoomMediaState(data);
      }
    };

    loadInitialState();

    const channel = supabase
      .channel(`room-media-state-${room.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'vcafe_room_media_state', filter: `room_id=eq.${room.id}` },
        payload => {
          if (!cancelled) {
            setRoomMediaState(payload.new);
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'vcafe_room_media_state', filter: `room_id=eq.${room.id}` },
        payload => {
          if (!cancelled) {
            setRoomMediaState(payload.new);
          }
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [room?.id, room?.isEvent, room?.hasLinkedRoom, supabase]);

  // Load list of documents for selection:
  // - All docs explicitly associated with this room via room_documents
  // - Plus any docs owned by the current user
  // - Plus a safety net for room.current_doc_id if not yet linked
  useEffect(() => {
    const loadDocs = async () => {
      if (!user?.id || !room?.id) return;

      try {
        const [ownRes, linksRes] = await Promise.all([
          supabase
            .from('vcafe_documents')
            .select('*')
            .eq('owner_id', user.id)
            .order('created_at', { ascending: true }),
          supabase
            .from('room_documents')
            .select('document_id')
            .eq('room_id', room.id),
        ]);

        const docsById = new Map();

        if (!ownRes.error && ownRes.data) {
          ownRes.data.forEach(doc => {
            if (doc && doc.id != null) {
              docsById.set(doc.id, doc);
            }
          });
        }

        if (!linksRes.error && linksRes.data && linksRes.data.length > 0) {
          const roomDocIds = linksRes.data
            .map(row => row.document_id)
            .filter(id => id != null);

          if (roomDocIds.length > 0) {
            const { data: roomDocs, error: roomDocsError } = await supabase
              .from('vcafe_documents')
              .select('*')
              .in('id', roomDocIds);

            if (!roomDocsError && roomDocs) {
              roomDocs.forEach(doc => {
                if (doc && doc.id != null) {
                  docsById.set(doc.id, doc);
                }
              });
            }
          }
        }

        // Safety net: ensure current_doc_id is included even if not yet in room_documents
        if (room?.current_doc_id && !docsById.has(room.current_doc_id)) {
          const { data: extra, error: extraError } = await supabase
            .from('vcafe_documents')
            .select('*')
            .eq('id', room.current_doc_id)
            .limit(1);

          if (!extraError && extra && extra[0] && extra[0].id != null) {
            docsById.set(extra[0].id, extra[0]);
          }
        }

        const mergedDocs = Array.from(docsById.values()).sort((a, b) => {
          const aTime = a?.created_at ? new Date(a.created_at).getTime() : 0;
          const bTime = b?.created_at ? new Date(b.created_at).getTime() : 0;
          return aTime - bTime;
        });

        setDocOptions(mergedDocs);
      } catch (err) {
        console.error('Error loading documents for room', err);
      }
    };

    loadDocs();
  }, [user?.id, room?.id, room?.current_doc_id, supabase]);

  // --- Docs metadata loading ---

  useEffect(() => {
    const loadDoc = async () => {
      if (!room?.current_doc_id) {
        setCurrentDoc(null);
        setCurrentDocPage(room?.current_doc_page || 1);
        return;
      }

      setCurrentDocPage(room?.current_doc_page || 1);

      const { data, error } = await supabase
        .from('vcafe_documents')
        .select('*')
        .eq('id', room.current_doc_id)
        .limit(1);

      if (error) {
        console.error('Error loading current doc metadata', error);
        setCurrentDoc(null);
        return;
      }

      const doc = (data && data[0]) || null;
      setCurrentDoc(doc);
    };

    loadDoc();
  }, [room?.current_doc_id, room?.current_doc_page, supabase]);

  const currentDocUrl = useMemo(() => {
    if (!currentDoc) return null;
    try {
      const { data } = supabase
        .storage
        .from(currentDoc.bucket || 'documents')
        .getPublicUrl(currentDoc.path);
      return data?.publicUrl || null;
    } catch {
      return null;
    }
  }, [currentDoc, supabase]);

  const upsertRoomMediaState = async (partialState) => {
    if (!room?.id || !user?.id) return;

    const baseState = roomMediaState || {
      room_id: room.id,
      layout_mode: layoutMode === 'presentation' ? 'presentation' : 'conference',
      presentation_layout: 'single',
      primary_surface_role: 'whiteboard',
      secondary_surface_role: 'none',
      active_media_surface: null,
      play_state: 'stopped',
      position_sec: 0,
      playback_rate: 1,
    };

    const nextState = {
      ...baseState,
      ...partialState,
      updated_by: user.id,
    };

    setRoomMediaState(nextState);

    try {
      await supabase
        .from('vcafe_room_media_state')
        .upsert(nextState, { onConflict: 'room_id' });
    } catch (err) {
      console.error('Error updating room media state', err);
    }
  };

  // --- Handlers ---

  const handleSendMessage = async (text) => {
    if (!text.trim()) return;
    await supabase.from('room_messages').insert([{
      room_id: room.id,
      user_id: user.id,
      content: text,
      created_at: new Date().toISOString(),
    }]);
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const isBgAudioPlaying = !!(
    bgAudioUrl &&
    roomMediaState?.bg_audio_media_id &&
    roomMediaState?.bg_audio_play_state === 'playing'
  );

  const startPresenting = async (mode) => {
    if (!room?.id || !user?.id) return;
    if (room?.isEvent && !room?.hasLinkedRoom) return;

    // Someone else is already presenting; do not take over
    if (presenterId && presenterId !== user.id) {
      pushToast('Someone is already presenting', 'error');
      return;
    }

    setLayoutMode('presentation');
    setPresentationContentType(mode);
    setPresenterId(user.id);

    if (mode === 'whiteboard') {
      upsertRoomMediaState({
        layout_mode: 'presentation',
        presentation_layout: 'single',
        primary_surface_role: 'whiteboard',
        secondary_surface_role: 'none',
      });
    } else if (mode === 'docs') {
      upsertRoomMediaState({
        layout_mode: 'presentation',
        presentation_layout: 'single',
        primary_surface_role: 'doc',
        secondary_surface_role: 'none',
      });
    } else if (mode === 'screen') {
      upsertRoomMediaState({
        layout_mode: 'presentation',
        presentation_layout: 'single',
        primary_surface_role: 'screen',
        secondary_surface_role: 'none',
      });
    } else if (mode === 'media') {
      upsertRoomMediaState({
        layout_mode: 'presentation',
        presentation_layout: 'single',
        primary_surface_role: 'media',
        secondary_surface_role: 'none',
      });
    } else if (mode === 'split') {
      const nextLayout = roomMediaState?.presentation_layout === 'split_vertical'
        ? 'split_vertical'
        : 'split_horizontal';

      const hasRoles = !!(roomMediaState?.primary_surface_role || roomMediaState?.secondary_surface_role);

      upsertRoomMediaState({
        layout_mode: 'presentation',
        presentation_layout: nextLayout,
        ...(hasRoles ? {} : {
          primary_surface_role: 'whiteboard',
          secondary_surface_role: 'screen',
        }),
      });
    }

    try {
      await supabase
        .from('rooms')
        .update({ presenter_id: user.id, presentation_mode: mode })
        .eq('id', room.id);
    } catch {
      // best-effort; local state still reflects presenting
    }
  };

  const stopPresenting = async () => {
    if (!room?.id || !isPresenter) return;
    if (room?.isEvent && !room?.hasLinkedRoom) return;

    setPresenterId(null);
    setLayoutMode('conference');
    setPresentationContentType('whiteboard');

    try {
      await supabase
        .from('rooms')
        .update({ presenter_id: null, presentation_mode: 'grid' })
        .eq('id', room.id);
    } catch {
      // best-effort
    }
  };

  const handleScreenToggle = () => {
    setScreenOn(v => {
      const nv = !v;
      pushToast(nv ? 'Screen share started' : 'Screen share stopped');
      if (nv) {
        // Starting screen share also starts presenting for this user if possible
        startPresenting('screen');
      }
      return nv;
    });
  };

  const handleBgAudioChangeMedia = (nextId) => {
    if (!isPresenter) return;
    upsertRoomMediaState({
      layout_mode: 'presentation',
      bg_audio_media_id: nextId || null,
      bg_audio_play_state: 'stopped',
      bg_audio_position_sec: 0,
      bg_audio_playback_rate: 1,
    });
  };

  const handleBgAudioPlaybackChange = (state) => {
    if (!isPresenter) return;
    upsertRoomMediaState({
      layout_mode: 'presentation',
      bg_audio_play_state: state.playState || 'stopped',
      bg_audio_position_sec: state.positionSec || 0,
      bg_audio_playback_rate: state.playbackRate || 1,
    });
  };

  // When someone else is presenting, keep audience locked in presentation mode
  useEffect(() => {
    if (someonePresenting && presenterId !== user.id) {
      setLayoutMode('presentation');
    }
  }, [someonePresenting, presenterId, user.id]);

  // --- Render Helpers ---

  const currentUser = user?.username || user?.email || 'You';

  const renderMainContentWithBgAudioIndicator = () => {
    const content = renderMainContent();

    if (
      !isAudience ||
      !isBgAudioPlaying ||
      layoutMode !== 'presentation' ||
      presentationContentType === 'split'
    ) {
      return content;
    }

    return (
      <div className="vc-main-with-bg-audio-indicator">
        <div className="vc-bg-audio-indicator-icon-only">🔊</div>
        {content}
      </div>
    );
  };

  const headerTitle = useMemo(() => {
    if (!room) return 'Virtual Café';
    if (room.isEvent) {
      if (typeof room.name === 'string') {
        // Strip the "(Event Room)" suffix we add when building eventRooms for card listings
        return room.name.replace(/\s*\(Event Room\)\s*$/i, '');
      }
      return room.title || 'Event Room';
    }
    // For regular and group-linked rooms, room.name should already be the meaningful name
    return room.name || 'Virtual Café';
  }, [room]);

  const localVideoRef = useRef(null); // Sidebar video: self in conference, presenter in presentation
  const pipVideoRef = useRef(null);   // PiP local camera preview (separate from sidebar)
  const stageVideoRef = useRef(null); // Main stage preview in conference grid/spotlight
  const screenVideoRef = useRef(null); // Screen share video element
  const bgAudioFollowerRef = useRef(null); // Hidden follower audio element for background audio
  const rawCamStreamRef = useRef(null);
  const camStreamRef = useRef(null); // Combined stream: processed video + original audio
  const screenStreamRef = useRef(null);
  const videoProcessorCanvasRef = useRef(null);
  const videoProcessorVideoRef = useRef(null);
  const videoProcessorRafRef = useRef(null);

  const audioContextRef = useRef(null);
  const analyserNodesRef = useRef(new Map());
  const audioSourceNodesRef = useRef(new Map());
  const audioRafIdRef = useRef(null);
  const localMicAnalyserRef = useRef(null);
  const localMicSourceRef = useRef(null);
  const localMicRafRef = useRef(null);

  // Load media settings on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('vc.mediaSettings');
      if (saved) {
        setMediaSettings(JSON.parse(saved));
      }
    } catch {
      // ignore
    }
  }, []);

  const videoBrightness = mediaSettings?.videoEnhancements?.brightness ?? 0;
  const videoContrast = mediaSettings?.videoEnhancements?.contrast ?? 0;
  const videoSaturation = mediaSettings?.videoEnhancements?.saturation ?? 0;
  const videoHue = mediaSettings?.videoEnhancements?.hue ?? 0;
  const sendEnhancedToPeers = mediaSettings?.videoEnhancements?.sendEnhancedToPeers ?? true;
  const micVolume = mediaSettings?.micEnhancements?.volume ?? 1;

  const selfVideoFilterStyle = useMemo(() => {
    return {};
  }, [videoBrightness, videoContrast, videoSaturation, videoHue]);

  // WebRTC: mediasoup SFU hook for remote streams
  const { remoteStreams, connected: rtcConnected, error: rtcError } = useVcafeWebRTC({
    roomId: room?.id,
    userId: user?.id,
    localStream: camStreamRef.current,
    micVolume,
  });

  // Merge remote streams into participant list for layouts
  const participantsWithStreams = useMemo(() => {
    return participantList.map(p => ({
      ...p,
      stream: remoteStreams.get(p.id) || null,
    }));
  }, [participantList, remoteStreams]);

  const sidebarDisplayName = useMemo(() => {
    if (layoutMode === 'presentation' && presenterId && presenterId !== user.id) {
      const presenter = participantsWithStreams.find(p => p.id === presenterId);
      return presenter?.name || currentUser;
    }
    return currentUser;
  }, [layoutMode, presenterId, user.id, participantsWithStreams, currentUser]);

  // Label for PiP: show presenter name when viewing another presenter in presentation mode
  const pipLabel = useMemo(() => {
    if (layoutMode === 'presentation' && presenterId && presenterId !== user.id) {
      const presenter = participantsWithStreams.find(p => p.id === presenterId);
      return presenter?.name || currentUser;
    }
    return currentUser;
  }, [layoutMode, presenterId, user.id, participantsWithStreams, currentUser]);

  useEffect(() => {
    const RemoteAudioContext = window.AudioContext || window.webkitAudioContext;
    if (!RemoteAudioContext) return;

    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new RemoteAudioContext();
      } catch {
        return;
      }
    }

    const audioCtx = audioContextRef.current;

    analyserNodesRef.current.forEach((analyser, uid) => {
      if (!remoteStreams.has(uid)) {
        const source = audioSourceNodesRef.current.get(uid);
        try {
          if (source) {
            source.disconnect();
          }
        } catch {
        }
        analyserNodesRef.current.delete(uid);
        audioSourceNodesRef.current.delete(uid);
      }
    });

    remoteStreams.forEach((stream, uid) => {
      if (!stream || analyserNodesRef.current.has(uid)) return;
      try {
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 128;
        source.connect(analyser);
        analyserNodesRef.current.set(uid, analyser);
        audioSourceNodesRef.current.set(uid, source);
      } catch {
      }
    });

    if (audioRafIdRef.current) {
      cancelAnimationFrame(audioRafIdRef.current);
      audioRafIdRef.current = null;
    }

    if (analyserNodesRef.current.size === 0) {
      setActiveSpeakerId(null);
      return;
    }

    const detect = () => {
      const dataArray = new Uint8Array(64);
      let maxVolume = 0;
      let loudestId = null;

      analyserNodesRef.current.forEach((analyser, uid) => {
        analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i += 1) {
          sum += dataArray[i];
        }
        const avg = sum / dataArray.length;
        if (avg > maxVolume) {
          maxVolume = avg;
          loudestId = uid;
        }
      });

      const threshold = 10;
      if (loudestId && maxVolume > threshold) {
        setActiveSpeakerId(prev => (prev === loudestId ? prev : loudestId));
      } else {
        setActiveSpeakerId(null);
      }

      audioRafIdRef.current = requestAnimationFrame(detect);
    };

    audioRafIdRef.current = requestAnimationFrame(detect);

    return () => {
      if (audioRafIdRef.current) {
        cancelAnimationFrame(audioRafIdRef.current);
        audioRafIdRef.current = null;
      }
    };
  }, [remoteStreams]);

  // Hidden background-audio follower player (audience hears synced audio without controls)
  useEffect(() => {
    const el = bgAudioFollowerRef.current;
    if (!el) return;

    const hasBgAudio = !!(bgAudioUrl && roomMediaState?.bg_audio_media_id);

    if (!hasBgAudio || !isAudience) {
      try {
        el.pause();
      } catch {
      }
      return;
    }

    const targetTime = bgAudioPositionSec || 0;
    if (Math.abs(targetTime - (el.currentTime || 0)) > 0.7) {
      try {
        el.currentTime = targetTime;
      } catch {
      }
    }

    const targetRate = bgAudioPlaybackRate || 1;
    if (el.playbackRate !== targetRate) {
      try {
        el.playbackRate = targetRate;
      } catch {
      }
    }

    if (bgAudioPlayState === 'playing' && el.paused) {
      el.play().catch(() => {});
    } else if ((bgAudioPlayState === 'paused' || bgAudioPlayState === 'stopped') && !el.paused) {
      el.pause();
    }
  }, [
    bgAudioUrl,
    bgAudioPlayState,
    bgAudioPositionSec,
    bgAudioPlaybackRate,
    roomMediaState?.bg_audio_media_id,
    isAudience,
  ]);

  // Local mic activity visualization
  useEffect(() => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    // Clean up any existing analyser / loop
    const cleanup = () => {
      setLocalMicActive(false);
      if (localMicRafRef.current) {
        cancelAnimationFrame(localMicRafRef.current);
        localMicRafRef.current = null;
      }
      if (localMicSourceRef.current) {
        try { localMicSourceRef.current.disconnect(); } catch (_) {}
        localMicSourceRef.current = null;
      }
      localMicAnalyserRef.current = null;
    };

    if (!micOn) {
      cleanup();
      return;
    }

    let cancelled = false;

    const startAnalyser = () => {
      if (cancelled) return;

      const stream = camStreamRef.current;
      const audioTrack = stream ? stream.getAudioTracks()[0] : null;

      // If the combined stream or its audio track is not ready yet, retry on the next frame
      if (!audioTrack) {
        localMicRafRef.current = requestAnimationFrame(startAnalyser);
        return;
      }

      let audioCtx = audioContextRef.current;
      if (!audioCtx) {
        try {
          audioCtx = new AudioCtx();
          audioContextRef.current = audioCtx;
        } catch {
          return;
        }
      }

      const mediaStream = new MediaStream([audioTrack]);
      const source = audioCtx.createMediaStreamSource(mediaStream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 128;
      source.connect(analyser);
      localMicSourceRef.current = source;
      localMicAnalyserRef.current = analyser;

      const dataArray = new Uint8Array(64);

      const detect = () => {
        if (cancelled || !localMicAnalyserRef.current) {
          setLocalMicActive(false);
          return;
        }
        localMicAnalyserRef.current.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i += 1) {
          sum += dataArray[i];
        }
        const avg = sum / dataArray.length;
        const threshold = 5; // slightly lower to make animation easier to trigger
        setLocalMicActive(avg > threshold);
        localMicRafRef.current = requestAnimationFrame(detect);
      };

      localMicRafRef.current = requestAnimationFrame(detect);
    };

    startAnalyser();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [micOn, camOn, screenOn, sendEnhancedToPeers]);

  // Participants ordering for sidebar list
  const orderedParticipants = useMemo(() => {
    const list = [...participantsWithStreams];

    // When there is a presenter, pin them first, then self, then others by name
    if (presenterId) {
      const presenter = list.find(p => p.id === presenterId);
      const self = list.find(p => p.isSelf && p.id !== presenterId);
      const others = list.filter(p => p.id !== presenterId && !p.isSelf);

      others.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      const ordered = [];
      if (presenter) ordered.push(presenter);
      if (self) ordered.push(self);
      return [...ordered, ...others];
    }

    // No presenter: put self (if present) first, then others by name
    const self = list.find(p => p.isSelf);
    const others = list.filter(p => !p.isSelf);
    others.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return self ? [self, ...others] : others;
  }, [participantsWithStreams, presenterId]);

  // Participants for the bottom strip in presentation mode
  const stripParticipants = useMemo(() => {
    if (layoutMode !== 'presentation' || !someonePresenting) {
      return [];
    }

    // Audience view: show self first, then everyone else except self
    if (presenterId !== user.id) {
      const selfTile = {
        id: user.id,
        name: currentUser,
        avatar: '👤',
        muted: !micOn,
        camOn,
        stream: camStreamRef.current || null,
      };
      const others = participantsWithStreams.filter(p => p.id !== user.id);
      return [selfTile, ...others];
    }

    // Presenter view: do not include self in strip, only other participants
    return participantsWithStreams.filter(p => p.id !== presenterId);
  }, [layoutMode, someonePresenting, presenterId, user.id, currentUser, micOn, camOn, participantsWithStreams]);

  // Attach local camera stream to sidebar and main-stage videos
  useEffect(() => {
    let cancelled = false;

    const applyEnhancementsToFrame = (imageData) => {
      const data = imageData.data;
      const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

      const b = videoBrightness || 0; // -1..1
      const c = videoContrast || 0;   // -1..1
      const s = videoSaturation || 0; // -1..1
      const h = videoHue || 0;        // -1..1

      const brightnessOffset = b * 255;
      const contrastFactor = 1 + c;
      const saturationAmount = s;
      const hueOffset = h * Math.PI; // -pi..pi

      const toHsl = (r, g, b2) => {
        r /= 255; g /= 255; b2 /= 255;
        const max = Math.max(r, g, b2);
        const min = Math.min(r, g, b2);
        let hVal = 0;
        const l = (max + min) / 2;
        const d = max - min;
        if (d === 0) {
          return { h: 0, s: 0, l };
        }
        const sVal = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r:
            hVal = (g - b2) / d + (g < b2 ? 6 : 0);
            break;
          case g:
            hVal = (b2 - r) / d + 2;
            break;
          default:
            hVal = (r - g) / d + 4;
        }
        hVal /= 6;
        return { h: hVal * 2 * Math.PI, s: sVal, l };
      };

      const fromHsl = (hVal, sVal, lVal) => {
        const hue2rgb = (p, q, t) => {
          if (t < 0) t += 1;
          if (t > 1) t -= 1;
          if (t < 1 / 6) return p + (q - p) * 6 * t;
          if (t < 1 / 2) return q;
          if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
          return p;
        };

        const hNorm = (hVal / (2 * Math.PI)) % 1;
        const sN = clamp01(sVal);
        const lN = clamp01(lVal);

        if (sN === 0) {
          const v = lN * 255;
          return [v, v, v];
        }

        const q = lN < 0.5 ? lN * (1 + sN) : lN + sN - lN * sN;
        const p = 2 * lN - q;
        const r = hue2rgb(p, q, hNorm + 1 / 3);
        const g = hue2rgb(p, q, hNorm);
        const b3 = hue2rgb(p, q, hNorm - 1 / 3);
        return [r * 255, g * 255, b3 * 255];
      };

      for (let i = 0; i < data.length; i += 4) {
        let r = data[i];
        let g = data[i + 1];
        let b4 = data[i + 2];

        r = r * contrastFactor + brightnessOffset;
        g = g * contrastFactor + brightnessOffset;
        b4 = b4 * contrastFactor + brightnessOffset;

        r = r < 0 ? 0 : r > 255 ? 255 : r;
        g = g < 0 ? 0 : g > 255 ? 255 : g;
        b4 = b4 < 0 ? 0 : b4 > 255 ? 255 : b4;

        let { h: hRad, s: sVal, l: lVal } = toHsl(r, g, b4);

        hRad += hueOffset;

        sVal = sVal + saturationAmount * sVal;

        const [r2, g2, b2] = fromHsl(hRad, sVal, lVal);

        data[i] = r2 < 0 ? 0 : r2 > 255 ? 255 : r2;
        data[i + 1] = g2 < 0 ? 0 : g2 > 255 ? 255 : g2;
        data[i + 2] = b2 < 0 ? 0 : b2 > 255 ? 255 : b2;
      }

      return imageData;
    };

    const ensureVideoProcessing = () => {
      if (!rawCamStreamRef.current) return;
      if (!sendEnhancedToPeers) {
        camStreamRef.current = rawCamStreamRef.current;
        return;
      }
      let videoEl = videoProcessorVideoRef.current;
      if (!videoEl) {
        videoEl = document.createElement('video');
        videoEl.autoplay = true;
        videoEl.playsInline = true;
        videoEl.muted = true;
        videoProcessorVideoRef.current = videoEl;
      }

      if (videoEl.srcObject !== rawCamStreamRef.current) {
        videoEl.srcObject = rawCamStreamRef.current;
        try { videoEl.play(); } catch (_) {}
      }

      let canvas = videoProcessorCanvasRef.current;
      if (!canvas) {
        canvas = document.createElement('canvas');
        videoProcessorCanvasRef.current = canvas;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const render = () => {
        if (cancelled) return;
        if (videoEl.readyState >= 2) {
          if (canvas.width !== videoEl.videoWidth || canvas.height !== videoEl.videoHeight) {
            canvas.width = videoEl.videoWidth || 640;
            canvas.height = videoEl.videoHeight || 360;
          }
          ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
          let frame;
          try {
            frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
          } catch (_) {
            frame = null;
          }
          if (frame) {
            const processed = applyEnhancementsToFrame(frame);
            ctx.putImageData(processed, 0, 0);
          }

          if (!camStreamRef.current) {
            const processedStream = canvas.captureStream(30);
            const videoTrack = processedStream.getVideoTracks()[0] || null;
            const combined = new MediaStream();
            if (videoTrack) combined.addTrack(videoTrack);
            if (rawCamStreamRef.current) {
              const audioTrack = rawCamStreamRef.current.getAudioTracks()[0];
              if (audioTrack) combined.addTrack(audioTrack);
            }
            camStreamRef.current = combined;
          }
        }
        videoProcessorRafRef.current = requestAnimationFrame(render);
      };

      if (!videoProcessorRafRef.current) {
        videoProcessorRafRef.current = requestAnimationFrame(render);
      }
    };
    const ensureCamera = async () => {
      try {
        if (!camOn) {
          if (camStreamRef.current) {
            camStreamRef.current.getVideoTracks().forEach(t => (t.enabled = false));
          }
          return;
        }

        if (!rawCamStreamRef.current) {
          if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return;
          const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
          if (cancelled) {
            stream.getTracks().forEach(t => t.stop());
            return;
          }
          rawCamStreamRef.current = stream;
        } else {
          rawCamStreamRef.current.getVideoTracks().forEach(t => (t.enabled = true));
        }

        // Sync mic state
        if (rawCamStreamRef.current) {
          rawCamStreamRef.current.getAudioTracks().forEach(t => (t.enabled = !!micOn));
        }

        ensureVideoProcessing();
        const bindTargets = () => {
          if (screenOn || !camStreamRef.current) return;

          // Do not override sidebar video when another user is presenting;
          // that case is handled by a dedicated sidebar effect.
          if (!someonePresenting || presenterId === user.id) {
            if (localVideoRef.current && localVideoRef.current.srcObject !== camStreamRef.current) {
              localVideoRef.current.srcObject = camStreamRef.current;
            }
          }

          if (pipVideoRef.current && pipVideoRef.current.srcObject !== camStreamRef.current) {
            pipVideoRef.current.srcObject = camStreamRef.current;
          }

          if (stageVideoRef.current && stageVideoRef.current.srcObject !== camStreamRef.current) {
            stageVideoRef.current.srcObject = camStreamRef.current;
          }
        };

        // Bind immediately, and again on next tick in case refs attach slightly later
        bindTargets();
        setTimeout(bindTargets, 0);
      } catch (e) {
        console.error('Error accessing camera/mic', e);
        pushToast('Unable to access camera/mic', 'error');
      }
    };

    ensureCamera();

    return () => {
      cancelled = true;
      if (videoProcessorRafRef.current) {
        cancelAnimationFrame(videoProcessorRafRef.current);
        videoProcessorRafRef.current = null;
      }
    };
  }, [
    camOn,
    micOn,
    screenOn,
    sidebarExpanded,
    layoutMode,
    focusMode,
    videoBrightness,
    videoContrast,
    videoSaturation,
    videoHue,
    sendEnhancedToPeers,
  ]);

  // Re-bind existing camera stream when layout/sidebar changes
  useEffect(() => {
    if (!camStreamRef.current || screenOn) return;

    // When another user is presenting, do not re-bind sidebar to local camera;
    // let the sidebar effect show the presenter's stream instead.
    if (!someonePresenting || presenterId === user.id) {
      if (localVideoRef.current && localVideoRef.current.srcObject !== camStreamRef.current) {
        localVideoRef.current.srcObject = camStreamRef.current;
      }
    }

    if (pipVideoRef.current && pipVideoRef.current.srcObject !== camStreamRef.current) {
      pipVideoRef.current.srcObject = camStreamRef.current;
    }

    if (stageVideoRef.current && stageVideoRef.current.srcObject !== camStreamRef.current) {
      stageVideoRef.current.srcObject = camStreamRef.current;
    }
  }, [sidebarExpanded, layoutMode, focusMode, screenOn]);

  // Bind PiP video: follow presenter in presentation mode, otherwise show local camera
  useEffect(() => {
    const videoEl = pipVideoRef.current;
    if (!videoEl) return;

    if (layoutMode === 'presentation' && presenterId && presenterId !== user.id) {
      const presenterStream = remoteStreams.get(presenterId);
      if (presenterStream && videoEl.srcObject !== presenterStream) {
        videoEl.srcObject = presenterStream;
      }
      return;
    }

    if (!screenOn && camStreamRef.current && videoEl.srcObject !== camStreamRef.current) {
      videoEl.srcObject = camStreamRef.current;
    }
  }, [layoutMode, presenterId, user.id, remoteStreams, screenOn]);

  // Sidebar video: show self in conference or when we are presenter;
  // show the active presenters remote stream when someone else is presenting.
  useEffect(() => {
    const videoEl = localVideoRef.current;
    if (!videoEl) return;

    // Presentation mode & another user is presenter: use their remote stream
    if (layoutMode === 'presentation' && presenterId && presenterId !== user.id) {
      const presenterStream = remoteStreams.get(presenterId);
      if (presenterStream && videoEl.srcObject !== presenterStream) {
        videoEl.srcObject = presenterStream;
      }
      return;
    }

    // Otherwise, fall back to local camera preview
    if (!screenOn && camStreamRef.current && videoEl.srcObject !== camStreamRef.current) {
      videoEl.srcObject = camStreamRef.current;
    }
  }, [layoutMode, presenterId, user.id, remoteStreams, screenOn]);

  // Manage screen share stream
  useEffect(() => {
    const startScreenShare = async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) return;
        let stream;
        try {
          // Prefer sharing a specific window when supported
          const constraints = {
            video: {
              displaySurface: 'window',
            },
            surfaceSwitching: 'include',
            selfBrowserSurface: 'exclude',
          };
          stream = await navigator.mediaDevices.getDisplayMedia(constraints);
        } catch (e) {
          // Fallback to generic screen share if advanced constraints are not supported or user cancels
          stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        }
        screenStreamRef.current = stream;
        if (screenVideoRef.current) {
          screenVideoRef.current.srcObject = stream;
        }
        // When user stops from browser UI, reset state
        const [track] = stream.getVideoTracks();
        if (track) {
          track.onended = () => {
            setScreenOn(false);
          };
        }
      } catch (e) {
        console.error('Error starting screen share', e);
        pushToast('Screen share failed or was cancelled', 'error');
        setScreenOn(false);
      }
    };

    if (screenOn) {
      startScreenShare();
      // While sharing, keep sidebar preview bound to camera if available
      if (localVideoRef.current && camStreamRef.current) {
        localVideoRef.current.srcObject = camStreamRef.current;
      }
      if (stageVideoRef.current && camStreamRef.current) {
        stageVideoRef.current.srcObject = camStreamRef.current;
      }
    } else {
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(t => t.stop());
        screenStreamRef.current = null;
      }
    }

    return () => {
      // Cleanup on unmount
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(t => t.stop());
        screenStreamRef.current = null;
      }
      if (camStreamRef.current) {
        camStreamRef.current.getTracks().forEach(t => t.stop());
        camStreamRef.current = null;
      }
      if (rawCamStreamRef.current) {
        rawCamStreamRef.current.getTracks().forEach(t => t.stop());
        rawCamStreamRef.current = null;
      }
      if (videoProcessorRafRef.current) {
        cancelAnimationFrame(videoProcessorRafRef.current);
        videoProcessorRafRef.current = null;
      }
    };
  }, [screenOn]);

  const renderSurface = (side) => {
    const isCurrentUserPresenter = presenterId === user?.id;
    const roleFromState = side === 'primary'
      ? roomMediaState?.primary_surface_role
      : roomMediaState?.secondary_surface_role;

    // Defaults preserve current behavior: Whiteboard + Screen in split mode
    const role = roleFromState || (side === 'primary' ? 'whiteboard' : 'screen');

    if (role === 'whiteboard') {
      return (
        <CafeWhiteboard
          roomId={room?.id}
          userId={user?.id}
          supabase={supabase}
          isPresenter={isCurrentUserPresenter}
          layoutMode="presentation"
        />
      );
    }

    if (role === 'screen') {
      return renderScreenShareContent();
    }

    if (role === 'doc' || role === 'image') {
      return (
        <CafeDocViewer
          docTitle={currentDoc?.title || 'No document selected'}
          page={currentDocPage || 1}
          docs={docOptions}
          selectedDocId={currentDoc?.id || null}
          onSelectDoc={handleSelectDoc}
          docUrl={currentDocUrl}
          onPageChange={handleDocPageChange}
          onTotalPagesChange={setTotalDocPages}
          onUploadDoc={handleUploadDocClick}
          onDeleteDoc={handleDeleteDoc}
          isPresenter={isCurrentUserPresenter}
          mountedDocs={mountedRoomDocs}
          activeDocId={activeRoomDocId}
          showDocsTray={showDocsTray}
          onToggleDocsTray={() => setShowDocsTray(v => !v)}
          onUnmountDoc={handleUnmountRoomDoc}
          docRotation={activeDocRotation}
          onToggleDocRotation={handleToggleDocRotation}
          roomId={room?.id}
          annotations={docAnnotations}
          onAddAnnotation={handleAddAnnotation}
          onDeleteAnnotation={handleDeleteAnnotation}
          onUpdateAnnotation={handleUpdateAnnotation}
          supabase={supabase}
          userId={user?.id}
        />
      );
    }

    if (role === 'media') {
      const mediaId = side === 'primary'
        ? roomMediaState?.primary_media_id
        : roomMediaState?.secondary_media_id;

      const media = mediaAssets.find(m => m.id === mediaId) || null;

      if (!media) {
        return (
          <div className="vc-screenshare-shell">
            <div className="vc-screenshare-idle">
              <div className="vc-screenshare-icon muted">🎵</div>
              <div className="vc-screenshare-text">No media selected</div>
            </div>
          </div>
        );
      }

      let publicUrl = null;
      try {
        const { data } = supabase
          .storage
          .from(media.bucket || 'media')
          .getPublicUrl(media.path);
        publicUrl = data?.publicUrl || null;
      } catch {
        publicUrl = null;
      }

      if (!publicUrl) {
        return (
          <div className="vc-screenshare-shell">
            <div className="vc-screenshare-idle">
              <div className="vc-screenshare-icon muted">🎵</div>
              <div className="vc-screenshare-text">Unable to load media</div>
            </div>
          </div>
        );
      }

      const activeSurface = roomMediaState?.active_media_surface || null;
      const globalPlayState = roomMediaState?.play_state || 'stopped';
      const globalPositionSec = roomMediaState?.position_sec || 0;
      const globalPlaybackRate = roomMediaState?.playback_rate || 1;

      const isLeader = isCurrentUserPresenter && activeSurface === side;
      const shouldFollow = !isCurrentUserPresenter && activeSurface === side;

      const canDeleteMedia = !!(media && user?.id && String(media.owner_id) === String(user.id));

      const handleChangeMedia = (nextId) => {
        if (!isCurrentUserPresenter) return;
        if (side === 'primary') {
          upsertRoomMediaState({
            layout_mode: 'presentation',
            primary_media_id: nextId || null,
          });
        } else {
          upsertRoomMediaState({
            layout_mode: 'presentation',
            secondary_media_id: nextId || null,
          });
        }
      };

      const handlePlaybackChange = (state) => {
        if (!isCurrentUserPresenter) return;
        upsertRoomMediaState({
          active_media_surface: side,
          play_state: state.playState || 'stopped',
          position_sec: state.positionSec || 0,
          playback_rate: state.playbackRate || 1,
        });
      };

      return (
        <CafeMediaPlayer
          title={media.title || media.filename}
          mediaKind={media.media_kind}
          src={publicUrl}
          isLeader={isLeader}
          shouldFollow={shouldFollow}
          playState={globalPlayState}
          positionSec={globalPositionSec}
          playbackRate={globalPlaybackRate}
          onPlaybackChange={handlePlaybackChange}
          isPresenter={isCurrentUserPresenter}
          mediaId={mediaId || null}
          mediaChoices={mediaChoices}
          onChangeMedia={handleChangeMedia}
          onUploadMedia={isCurrentUserPresenter ? handleUploadMediaClick : undefined}
          canDeleteMedia={canDeleteMedia}
          onDeleteMedia={handleDeleteMedia}
          rewindSteps={[2, 5, 10]}
        />
      );
    }

    return (
      <div className="vc-screenshare-shell">
        <div className="vc-screenshare-idle">
          <div className="vc-screenshare-text">No content selected</div>
        </div>
      </div>
    );
  };

  // Determine Main Content based on modes
  const renderMainContent = () => {
    if (layoutMode === 'presentation') {
      if (presentationContentType === 'whiteboard') {
        const isCurrentUserPresenter = presenterId === user?.id;
        return (
          <CafeWhiteboard
            roomId={room?.id}
            userId={user?.id}
            supabase={supabase}
            isPresenter={isCurrentUserPresenter}
            layoutMode="presentation"
          />
        );
      }

      if (presentationContentType === 'docs') {
        const isCurrentUserPresenter = presenterId === user?.id;
        return (
          <CafeDocViewer
            docTitle={currentDoc?.title || 'No document selected'}
            page={currentDocPage || 1}
            docs={docOptions}
            selectedDocId={currentDoc?.id || null}
            onSelectDoc={handleSelectDoc}
            docUrl={currentDocUrl}
            onPageChange={handleDocPageChange}
            onTotalPagesChange={setTotalDocPages}
            onUploadDoc={handleUploadDocClick}
            onDeleteDoc={handleDeleteDoc}
            isPresenter={isCurrentUserPresenter}
            mountedDocs={mountedRoomDocs}
            activeDocId={activeRoomDocId}
            showDocsTray={showDocsTray}
            onToggleDocsTray={() => setShowDocsTray(v => !v)}
            onUnmountDoc={handleUnmountRoomDoc}
            docRotation={activeDocRotation}
            onToggleDocRotation={handleToggleDocRotation}
            roomId={room?.id}
            annotations={docAnnotations}
            onAddAnnotation={handleAddAnnotation}
            onDeleteAnnotation={handleDeleteAnnotation}
            onUpdateAnnotation={handleUpdateAnnotation}
            supabase={supabase}
            userId={user?.id}
          />
        );
      }

      if (presentationContentType === 'media') {
        // Single-surface media presentation uses the primary surface
        return renderSurface('primary');
      }

      if (presentationContentType === 'split') {
        const splitOrientation = roomMediaState?.presentation_layout === 'split_vertical'
          ? 'vertical'
          : 'horizontal';

        const primaryRole = roomMediaState?.primary_surface_role || 'whiteboard';
        const secondaryRole = roomMediaState?.secondary_surface_role || 'screen';

        const roleLabel = (role) => {
          if (role === 'whiteboard') return 'Whiteboard';
          if (role === 'doc' || role === 'image') return 'Document';
          if (role === 'screen') return 'Screen Share';
          if (role === 'media') return 'Media';
          return 'Surface';
        };

        const primaryMedia = primaryRole === 'media' && roomMediaState?.primary_media_id
          ? mediaAssets.find(m => m.id === roomMediaState.primary_media_id) || null
          : null;
        const secondaryMedia = secondaryRole === 'media' && roomMediaState?.secondary_media_id
          ? mediaAssets.find(m => m.id === roomMediaState.secondary_media_id) || null
          : null;

        const primaryTitle = (() => {
          if (primaryRole === 'doc' || primaryRole === 'image') {
            return currentDoc?.title || currentDoc?.filename || null;
          }
          if (primaryRole === 'media' && primaryMedia) {
            return primaryMedia.title || primaryMedia.filename || null;
          }
          return null;
        })();

        const secondaryTitle = (() => {
          if (secondaryRole === 'doc' || secondaryRole === 'image') {
            return currentDoc?.title || currentDoc?.filename || null;
          }
          if (secondaryRole === 'media' && secondaryMedia) {
            return secondaryMedia.title || secondaryMedia.filename || null;
          }
          return null;
        })();

        const formatSurfaceTitle = (role, title) => {
          const base = roleLabel(role);
          if (!title) return base;
          const trimmed = title.length > 40 ? `${title.slice(0, 37)}…` : title;
          return `${base}: ${trimmed}`;
        };

        return (
          <div className="vc-presentation-split">
            <SplitViewLayout
              orientation={splitOrientation}
              leftTitle={formatSurfaceTitle(primaryRole, primaryTitle)}
              rightTitle={formatSurfaceTitle(secondaryRole, secondaryTitle)}
              leftContent={renderSurface('primary')}
              rightContent={renderSurface('secondary')}
            />
          </div>
        );
      }

      // Screen-share only view
      return renderScreenShareContent();
    }

    // Conference Mode
    if (focusMode === 'spotlight') {
      const spotlightParticipants = participantsWithStreams.filter(p => !p.isSelf);
      return (
        <SpotlightLayout
          participants={spotlightParticipants}
          currentUser={{ name: currentUser }}
          micOn={micOn}
          camOn={camOn}
          activeSpeakerId={activeSpeakerId}
          selfVideoRef={stageVideoRef}
          selfVideoStyle={selfVideoFilterStyle}
          selfMicActive={localMicActive}
          onMicToggle={() => setMicOn(m => !m)}
          onCamToggle={() => setCamOn(c => !c)}
        />
      );
    }

    const gridParticipants = participantsWithStreams.filter(p => !p.isSelf);
    return (
      <GridViewLayout
        participants={gridParticipants}
        currentUser={{ name: currentUser }}
        micOn={micOn}
        camOn={camOn}
        selfVideoRef={stageVideoRef}
        selfVideoStyle={selfVideoFilterStyle}
        onMicToggle={() => setMicOn(m => !m)}
        onCamToggle={() => setCamOn(c => !c)}
        selfMicActive={localMicActive}
      />
    );
  };

  const renderScreenShareContent = () => (
    <div className="vc-screenshare-shell">
      {screenOn && (
        <video
          ref={screenVideoRef}
          autoPlay
          playsInline
          className="vc-screenshare-video"
        />
      )}
      {!screenOn && (
        <div className="vc-screenshare-idle">
          <div className="vc-screenshare-icon muted">📺</div>
          <div className="vc-screenshare-text">No Screen Share</div>
          <button className="btn-primary" onClick={handleScreenToggle}>
            Start Sharing
          </button>
        </div>
      )}
    </div>
  );

  // Toolbar Content
  const renderToolbar = () => {
    const handleConferenceClick = () => {
      if (!someonePresenting) {
        // Normal conference mode
        setLayoutMode('conference');
        return;
      }

      if (isPresenter) {
        // Presenter clicking Conference stops presenting for everyone
        stopPresenting();
      } else {
        // Audience cannot leave presentation while someone is presenting
        pushToast('You are viewing a presentation. The presenter controls when it ends.', 'info');
      }
    };

    const handlePresentationClick = () => {
      if (!someonePresenting) {
        // No active presenter: start with whiteboard
        startPresenting('whiteboard');
      } else if (isPresenter) {
        // Already the presenter: ensure layout is presentation
        setLayoutMode('presentation');
      } else {
        // Audience automatically follows; nothing to do
        pushToast('You are following the current presenter.', 'info');
      }
    };

    const canChangePresentationContent = isPresenter;
    const audienceLocked = someonePresenting && !isPresenter;
    const splitOrientation = roomMediaState?.presentation_layout === 'split_vertical'
      ? 'vertical'
      : 'horizontal';

    const effectivePrimaryRole = roomMediaState?.primary_surface_role
      || (presentationContentType === 'whiteboard' ? 'whiteboard'
        : presentationContentType === 'docs' ? 'doc'
        : presentationContentType === 'screen' ? 'screen'
        : presentationContentType === 'media' ? 'media'
        : 'whiteboard');

    const effectiveSecondaryRole = roomMediaState?.secondary_surface_role
      || (presentationContentType === 'split' ? 'screen' : 'none');

    const handleSetPrimaryRole = (role) => {
      if (!isPresenter) return;
      upsertRoomMediaState({
        layout_mode: 'presentation',
        primary_surface_role: role,
      });
    };

    const handleSetSecondaryRole = (role) => {
      if (!isPresenter) return;
      upsertRoomMediaState({
        layout_mode: 'presentation',
        secondary_surface_role: role,
      });
    };

    const handleSetPrimaryMedia = (id) => {
      if (!isPresenter) return;
      upsertRoomMediaState({
        layout_mode: 'presentation',
        primary_media_id: id || null,
      });
    };

    const handleSetSecondaryMedia = (id) => {
      if (!isPresenter) return;
      upsertRoomMediaState({
        layout_mode: 'presentation',
        secondary_media_id: id || null,
      });
    };

    const handleToggleSplit = () => {
      if (!canChangePresentationContent) return;

      if (presentationContentType === 'split') {
        // Go back to a single-surface mode based on the current primary role
        const role = roomMediaState?.primary_surface_role || 'whiteboard';
        let nextMode = 'whiteboard';
        if (role === 'doc' || role === 'image') {
          nextMode = 'docs';
        } else if (role === 'screen') {
          nextMode = 'screen';
        } else if (role === 'media') {
          nextMode = 'media';
        }
        startPresenting(nextMode);
      } else {
        startPresenting('split');
      }
    };

    return (
      <>
        <div className="vc-toolbar-group">
          <button
            className={`vc-tool ${layoutMode === 'conference' && !someonePresenting ? 'active' : ''} ${audienceLocked ? 'disabled' : ''}`}
            onClick={audienceLocked ? undefined : handleConferenceClick}
            title={audienceLocked ? 'Conference mode is locked while someone is presenting' : 'Conference Mode'}
            disabled={audienceLocked}
          >
            ▦
          </button>
          <button
            className={`vc-tool ${layoutMode === 'presentation' ? 'active' : ''} ${audienceLocked ? 'disabled' : ''}`}
            onClick={audienceLocked ? undefined : handlePresentationClick}
            title={audienceLocked ? 'Only the presenter can control presentation mode' : 'Presentation Mode'}
            disabled={audienceLocked}
          >
            🎨
          </button>
        </div>

        {layoutMode === 'conference' && !someonePresenting && (
          <div className="vc-toolbar-group">
            <button
              className={`vc-tool ${focusMode === 'grid' ? 'active' : ''}`}
              onClick={() => setFocusMode('grid')}
              title="Grid view"
            >
              ⬚
            </button>
            <button
              className={`vc-tool ${focusMode === 'spotlight' ? 'active' : ''}`}
              onClick={() => setFocusMode('spotlight')}
              title="Spotlight view"
            >
              💡
            </button>
          </div>
        )}

        {layoutMode === 'presentation' && (
          <>
            {/* Layout toggle: single vs split (presenter only) */}
            {isPresenter && (
              <div className="vc-toolbar-group">
                <button
                  className={`vc-tool ${presentationContentType === 'split' ? 'active' : ''}`}
                  onClick={handleToggleSplit}
                  title="Single / Split view"
                >
                  ⇳
                </button>
              </div>
            )}

            {/* Background audio toggle (single-view only, presenter only). Uses a distinct icon (🎶) from the media-only control (🎵). */}
            {isPresenter && presentationContentType !== 'split' && (
              <div className="vc-toolbar-group">
                <button
                  className={`vc-tool ${showAudioPanel ? 'active' : ''}`}
                  onClick={() => setShowAudioPanel(v => !v)}
                  title="Background audio (play audio while presenting)"
                >
                  🎶
                </button>
              </div>
            )}

            {/* Split-view orientation controls (only in split view, near toggle) */}
            {isPresenter && presentationContentType === 'split' && (
              <div className="vc-toolbar-group">
                <button
                  className={`vc-tool ${splitOrientation === 'horizontal' ? 'active' : ''}`}
                  onClick={() => upsertRoomMediaState({
                    layout_mode: 'presentation',
                    presentation_layout: 'split_horizontal',
                  })}
                  title="Left / right layout"
                >
                  ⇆
                </button>
                <button
                  className={`vc-tool ${splitOrientation === 'vertical' ? 'active' : ''}`}
                  onClick={() => upsertRoomMediaState({
                    layout_mode: 'presentation',
                    presentation_layout: 'split_vertical',
                  })}
                  title="Top / bottom layout"
                >
                  ⇵
                </button>
              </div>
            )}

            {/* Single-surface presentation type selection (whiteboard / docs / screen / media) */}
            {presentationContentType !== 'split' && (
              <div className="vc-toolbar-group">
                <button
                  className={`vc-tool ${presentationContentType === 'whiteboard' ? 'active' : ''} ${!canChangePresentationContent ? 'disabled' : ''}`}
                  onClick={canChangePresentationContent ? () => startPresenting('whiteboard') : undefined}
                  title={canChangePresentationContent ? 'Whiteboard' : 'Only the presenter can change presentation content'}
                  disabled={!canChangePresentationContent}
                >
                  🧑‍🏫
                </button>
                <button
                  className={`vc-tool ${presentationContentType === 'docs' ? 'active' : ''} ${!canChangePresentationContent ? 'disabled' : ''}`}
                  onClick={canChangePresentationContent ? () => {
                    if (presentationContentType === 'docs') {
                      setShowDocsTray(v => !v);
                      return;
                    }
                    setShowDocsTray(false);
                    startPresenting('docs');
                  } : undefined}
                  title={canChangePresentationContent ? 'Documents / Slides (in-app)' : 'Only the presenter can change presentation content'}
                  disabled={!canChangePresentationContent}
                >
                  📄
                </button>
                <button
                  className={`vc-tool ${presentationContentType === 'screen' ? 'active' : ''} ${!canChangePresentationContent ? 'disabled' : ''}`}
                  onClick={canChangePresentationContent ? handleScreenToggle : undefined}
                  title={canChangePresentationContent ? 'Screen Share' : 'Only the presenter can change presentation content'}
                  disabled={!canChangePresentationContent}
                >
                  📺
                </button>
                <button
                  className={`vc-tool ${presentationContentType === 'media' ? 'active' : ''} ${!canChangePresentationContent ? 'disabled' : ''}`}
                  onClick={canChangePresentationContent ? () => startPresenting('media') : undefined}
                  title={canChangePresentationContent ? 'Media only (audio / video)' : 'Only the presenter can change presentation content'}
                  disabled={!canChangePresentationContent}
                >
                  🎵
                </button>
              </div>
            )}

            {/* Single-surface media controls moved into CafeMediaPlayer header */}

            {/* Split-view main surface controls (only in split view) */}
            {isPresenter && presentationContentType === 'split' && (
              <div className="vc-toolbar-group">
                <button
                  className={`vc-tool ${effectivePrimaryRole === 'whiteboard' ? 'active' : ''}`}
                  onClick={() => handleSetPrimaryRole('whiteboard')}
                  title="Main surface: Whiteboard"
                >
                  🧑‍🏫
                </button>
                <button
                  className={`vc-tool ${effectivePrimaryRole === 'doc' ? 'active' : ''}`}
                  onClick={() => handleSetPrimaryRole('doc')}
                  title="Main surface: Document / Image"
                >
                  📄
                </button>
                <button
                  className={`vc-tool ${effectivePrimaryRole === 'screen' ? 'active' : ''}`}
                  onClick={() => handleSetPrimaryRole('screen')}
                  title="Main surface: Screen Share"
                >
                  📺
                </button>
                <button
                  className={`vc-tool ${effectivePrimaryRole === 'media' ? 'active' : ''}`}
                  onClick={() => handleSetPrimaryRole('media')}
                  title="Main surface: Media (audio / video)"
                >
                  🎵
                </button>
              </div>
            )}

            {/* Split-view secondary surface controls (only in split view) */}
            {isPresenter && presentationContentType === 'split' && (
              <div className="vc-toolbar-group">
                <button
                  className={`vc-tool ${effectiveSecondaryRole === 'whiteboard' ? 'active' : ''}`}
                  onClick={() => handleSetSecondaryRole('whiteboard')}
                  title="Second surface: Whiteboard"
                >
                  🧑‍🏫
                </button>
                <button
                  className={`vc-tool ${effectiveSecondaryRole === 'doc' ? 'active' : ''}`}
                  onClick={() => handleSetSecondaryRole('doc')}
                  title="Second surface: Document / Image"
                >
                  📄
                </button>
                <button
                  className={`vc-tool ${effectiveSecondaryRole === 'screen' ? 'active' : ''}`}
                  onClick={() => handleSetSecondaryRole('screen')}
                  title="Second surface: Screen Share"
                >
                  📺
                </button>
                <button
                  className={`vc-tool ${effectiveSecondaryRole === 'media' ? 'active' : ''}`}
                  onClick={() => handleSetSecondaryRole('media')}
                  title="Second surface: Media (audio / video)"
                >
                  🎵
                </button>
              </div>
            )}

            {/* Split-view orientation controls moved above, next to toggle */}

            {/* Stop presenting (explicit control for presenter) */}
            {isPresenter && (
              <button
                className="vc-tool"
                onClick={stopPresenting}
                title="Stop presenting and return to conference"
              >
                ⏹
              </button>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <>
      <UnifiedRoomLayout
        mode={layoutMode}
        sidebarExpanded={sidebarExpanded}
        onToggleSidebar={() => setSidebarExpanded(v => !v)}

        localVideoRef={localVideoRef}
        currentUser={sidebarDisplayName}
        localVideoStyle={selfVideoFilterStyle}
        micOn={micOn}
        camOn={camOn}
        onMicToggle={() => setMicOn(m => !m)}
        onCamToggle={() => setCamOn(c => !c)}
        onOpenDeviceSettings={() => openMediaSettings?.()}
        localMicActive={localMicActive}

        mainContent={renderMainContentWithBgAudioIndicator()}

        showBottomStrip={layoutMode === 'presentation'}
        bottomStripExpanded={bottomStripExpanded}
        onToggleBottomStrip={() => setBottomStripExpanded(v => !v)}
        otherParticipants={stripParticipants}

        messages={messages}
        participants={orderedParticipants}
        presenterId={presenterId}
        onSendMessage={handleSendMessage}

        sessionTime={formatTime(seconds)}
        headerTitle={headerTitle}
        onLeave={onLeave}

        bottomToolbar={renderToolbar()}
      />

      {isPresenter && layoutMode === 'presentation' && presentationContentType !== 'split' && showAudioPanel && (
        <div className="vc-bg-audio-panel">
          <CafeMediaPlayer
            title={bgAudioMedia?.title || bgAudioMedia?.filename || 'Background audio'}
            mediaKind="audio"
            src={bgAudioUrl}
            isLeader
            shouldFollow={false}
            playState={bgAudioPlayState}
            positionSec={bgAudioPositionSec}
            playbackRate={bgAudioPlaybackRate}
            onPlaybackChange={handleBgAudioPlaybackChange}
            isPresenter
            mediaId={bgAudioMediaId || null}
            mediaChoices={audioMediaChoices}
            onChangeMedia={handleBgAudioChangeMedia}
            onUploadMedia={handleUploadMediaClick}
            canDeleteMedia={!!(bgAudioMedia && user?.id && String(bgAudioMedia.owner_id) === String(user.id))}
            onDeleteMedia={handleDeleteMedia}
            rewindSteps={[2, 5, 10]}
          />
          <button
            type="button"
            className="vc-bg-audio-panel-close"
            onClick={() => setShowAudioPanel(false)}
          >
            ✕
          </button>
        </div>
      )}

      {/* Hidden file input for Docs upload */}
      <input
        type="file"
        accept="application/pdf,image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleUploadDocFileChange}
      />

      {/* Hidden file input for Media upload */}
      <input
        type="file"
        accept="audio/*,video/*"
        ref={mediaFileInputRef}
        style={{ display: 'none' }}
        onChange={handleUploadMediaFileChange}
      />

      {/* PiP Video when sidebar is collapsed (kept mounted to preserve position) */}
      <PiPVideoFeed
        videoRef={pipVideoRef}
        currentUser={pipLabel}
        micOn={micOn}
        camOn={camOn}
        onMicToggle={() => setMicOn(v => !v)}
        onCamToggle={() => setCamOn(v => !v)}
        onExpand={() => setSidebarExpanded(true)}
        visible={!sidebarExpanded}
      />

      {isAudience && (
        <audio
          ref={bgAudioFollowerRef}
          src={bgAudioUrl || undefined}
          autoPlay
          style={{ display: 'none' }}
        />
      )}

      {/* MediaDeviceSettings removed - now using global MediaSettingsModal from header */}

      <ToastStack toasts={toasts} onClose={closeToast} />
    </>
  );
};

export default VideoChatRoom;